export type LegacyStartupAsset = Readonly<{ url: string; bytes: number }>;

export const LEGACY_LOGIN_ASSETS: readonly LegacyStartupAsset[] = [
  { url: '/legacy/startup/loadbar_overlay.png', bytes: 2_295 },
  { url: '/legacy/startup/loginbackdrop_eng.jpg', bytes: 55_993 },
  { url: '/legacy/startup/accountbg.png', bytes: 11_551 },
  { url: '/legacy/startup/button_big_normal.png', bytes: 1_354 },
  { url: '/legacy/startup/button_big_over.png', bytes: 1_398 },
  { url: '/legacy/startup/button_big_down.png', bytes: 1_374 },
  { url: '/legacy/startup/button_big_disabled.png', bytes: 1_372 },
  { url: '/legacy/startup/cursor.png', bytes: 1_641 },
  { url: '/legacy/startup/cursor2.png', bytes: 1_477 },
  { url: '/legacy/startup/cursor3.png', bytes: 2_044 },
  { url: '/legacy/startup/cursor4.png', bytes: 967 },
  { url: '/legacy/startup/cursor5.png', bytes: 1_698 },
  { url: '/legacy/startup/cursor6.png', bytes: 1_698 },
  { url: '/legacy/startup/cursor7.png', bytes: 1_634 },
  { url: '/legacy/startup/cursor8.png', bytes: 1_676 },
  { url: '/legacy/startup/cursor9.png', bytes: 1_098 },
  { url: '/legacy/startup/cursor10.png', bytes: 1_103 },
  { url: '/legacy/startup/cursor11.png', bytes: 1_343 },
  { url: '/legacy/startup/cursor12.png', bytes: 988 },
  { url: '/legacy/startup/DIN-Medium.ttf', bytes: 21_268 },
] as const;

export type LegacyStartupDownloadState =
  | Readonly<{ phase: 'idle'; percent: 0 }>
  | Readonly<{ phase: 'loading'; percent: number }>
  | Readonly<{ phase: 'ready'; percent: 100 }>
  | Readonly<{ phase: 'failed'; percent: number }>;

type StartupAssetFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

type StartupDownloadListener = (state: LegacyStartupDownloadState) => void;

/**
 * Gates login on the small source startup package only. World assets remain demand-loaded by the
 * spatial streamer; treating them as launcher updates would violate the browser streaming model.
 */
export class LegacyStartupAssetLoader {
  private state: LegacyStartupDownloadState = { phase: 'idle', percent: 0 };
  private readonly listeners = new Set<StartupDownloadListener>();
  private activeRun = 0;
  private currentLoad: Promise<void> | undefined;

  constructor(
    private readonly assets: readonly LegacyStartupAsset[] = LEGACY_LOGIN_ASSETS,
    private readonly fetchAsset: StartupAssetFetch = globalThis.fetch.bind(globalThis),
  ) {
    if (assets.length === 0) throw new Error('Legacy startup asset package must not be empty');
    if (assets.some((asset) => asset.url.length === 0 || !Number.isSafeInteger(asset.bytes))) {
      throw new Error('Legacy startup asset package contains invalid metadata');
    }
    if (assets.some((asset) => asset.bytes <= 0)) {
      throw new Error('Legacy startup asset package contains an empty asset');
    }
  }

  currentState(): LegacyStartupDownloadState {
    return this.state;
  }

  subscribe(listener: StartupDownloadListener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  start(): Promise<void> {
    if (this.state.phase === 'ready') return Promise.resolve();
    if (this.currentLoad !== undefined) return this.currentLoad;
    const cacheMode: RequestCache = this.state.phase === 'failed' ? 'reload' : 'force-cache';
    const run = ++this.activeRun;
    this.update({ phase: 'loading', percent: 0 });
    const load = this.load(run, cacheMode);
    this.currentLoad = load;
    return load;
  }

  retry(): Promise<void> {
    if (this.state.phase === 'loading') return this.currentLoad ?? Promise.resolve();
    this.currentLoad = undefined;
    return this.start();
  }

  private async load(run: number, cacheMode: RequestCache): Promise<void> {
    const totalBytes = this.assets.reduce((sum, asset) => sum + asset.bytes, 0);
    let completedBytes = 0;
    const recordBytes = (bytes: number): void => {
      if (run !== this.activeRun || this.state.phase !== 'loading') return;
      completedBytes += bytes;
      this.update({
        phase: 'loading',
        percent: Math.min(99, Math.floor((completedBytes * 100) / totalBytes)),
      });
    };
    try {
      await Promise.all(
        this.assets.map(async (asset) => {
          const response = await this.fetchAsset(asset.url, {
            cache: cacheMode,
            credentials: 'same-origin',
          });
          if (!response.ok) {
            throw new Error(`Legacy startup asset request failed with ${String(response.status)}`);
          }
          const receivedBytes = await readResponseBytes(response, recordBytes);
          if (receivedBytes !== asset.bytes) {
            throw new Error('Legacy startup asset size does not match its source package');
          }
        }),
      );
      if (run === this.activeRun) this.update({ phase: 'ready', percent: 100 });
    } catch {
      if (run === this.activeRun) {
        this.update({
          phase: 'failed',
          percent: Math.min(99, Math.floor((completedBytes * 100) / totalBytes)),
        });
      }
    } finally {
      if (run === this.activeRun) this.currentLoad = undefined;
    }
  }

  private update(state: LegacyStartupDownloadState): void {
    this.state = state;
    for (const listener of this.listeners) listener(state);
  }
}

export const sharedLegacyStartupAssetLoader = new LegacyStartupAssetLoader();

async function readResponseBytes(
  response: Response,
  recordBytes: (bytes: number) => void,
): Promise<number> {
  if (response.body === null) {
    const bytes = (await response.arrayBuffer()).byteLength;
    recordBytes(bytes);
    return bytes;
  }
  const reader = response.body.getReader();
  let received = 0;
  for (;;) {
    const result = await reader.read();
    if (result.done) return received;
    received += result.value.byteLength;
    recordBytes(result.value.byteLength);
  }
}
